import { i18n } from '../internationalize/i18n.service'
import { AiChatService, type ChatTurn, type ChatSource } from './ai-chat.service'

// Conversational assistant panel for the webapp home. Meant to be dropped above
// the article feed as a Webflow embed: `<tbw-ai-chat></tbw-ai-chat>`.
//
// Multi-turn: keeps the full transcript and replays it as `history` on each
// request. Shadow DOM so Webflow's page styles can't leak in (and vice-versa).

type Lang = 'en' | 'fr'

const COPY: Record<Lang, Record<string, string>> = {
  en: {
    title: 'The Big Whale Assistant',
    subtitle: 'Ask about research, markets, events or regulation.',
    placeholder: 'Ask anything about digital assets…',
    send: 'Send',
    thinking: 'Thinking…',
    error: 'Something went wrong. Please try again.',
    disclaimer: 'AI assistant — can make mistakes. Verify important information.',
    sources: 'Sources',
    reset: 'New conversation',
    hide: 'Hide',
    show: 'Show assistant',
    chip_research: 'Latest research',
    chip_mica: 'MiCA licences',
    chip_stablecoins: 'Stablecoins',
    chip_events: 'Upcoming events',
    q_research: 'What are the latest research reports and analyses from The Big Whale?',
    q_mica: 'Which companies hold a MiCA licence, and how do I explore the licence tracker?',
    q_stablecoins: 'What is The Big Whale’s latest coverage on stablecoins?',
    q_events: 'What are the upcoming Big Whale events?',
  },
  fr: {
    title: 'L’assistant The Big Whale',
    subtitle: 'Posez vos questions : recherche, marchés, événements, régulation.',
    placeholder: 'Posez une question sur les actifs numériques…',
    send: 'Envoyer',
    thinking: 'Réflexion…',
    error: 'Une erreur est survenue. Réessayez.',
    disclaimer: 'Assistant IA — peut se tromper. Vérifiez les informations importantes.',
    sources: 'Sources',
    reset: 'Nouvelle conversation',
    hide: 'Masquer',
    show: 'Afficher l’assistant',
    chip_research: 'Dernières recherches',
    chip_mica: 'Licences MiCA',
    chip_stablecoins: 'Stablecoins',
    chip_events: 'Événements à venir',
    q_research: 'Quelles sont les dernières recherches et analyses de The Big Whale ?',
    q_mica: 'Quelles entreprises détiennent une licence MiCA, et comment explorer le tracker des licences ?',
    q_stablecoins: 'Quelle est l’actualité la plus récente de The Big Whale sur les stablecoins ?',
    q_events: 'Quels sont les prochains événements The Big Whale ?',
  },
}

// Persisted so a user who hides the assistant keeps it hidden across navigations.
const COLLAPSE_KEY = 'tbw-ai-chat-collapsed'

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ))

// Build a regex source that matches a source title in prose case-, accent- and
// punctuation-insensitively — so a canonical tracker name links the model's
// (often reformatted) rendering. Letters expand to their accented variants;
// separators become flexible; the whole thing runs with the `i` flag.
const ACCENT_CLASS: Record<string, string> = {
  a: 'aàâäá', c: 'cç', e: 'eéèêë', i: 'iîïí', o: 'oôöó', u: 'uûüù', n: 'nñ',
}
// Platform section pages — when the assistant names an area ("in the Reports
// section", "on the Trackers"), link it to the real app page. Matched by exact
// case + word boundary so generic lowercase words (e.g. "annual reports") aren't
// linked. (No dedicated landing for Trackers/Licences/Academy → best mapping.)
const APP = 'https://app.thebigwhale.io'
const SECTION_LINKS: Array<[RegExp, string]> = [
  [/\bReports\b/, `${APP}/all-reports`],
  [/\bRapports\b/, `${APP}/all-reports`],
  [/\bBriefings\b/, `${APP}/all-briefings`],
  [/\bDashboards\b/, `${APP}/all-dashboards`],
  [/\bTrackers\b/, `${APP}/maps/people`],
  [/\bPeople\b/, `${APP}/maps/people`],
  [/\bCompanies\b/, `${APP}/maps/companies`],
  [/\bNews\b/, `${APP}/news`],
  [/\bContents\b/, `${APP}/content`],
  [/\bContenus\b/, `${APP}/content`],
]

// True if `offset` in `text` sits inside an already-inserted markdown link
// (the [label] or the (url) part), so we never nest links.
const insideLink = (text: string, offset: number): boolean => {
  const before = text.slice(0, offset)
  const opens = (before.match(/\[/g) || []).length
  const closes = (before.match(/\]\(/g) || []).length
  if (opens > closes) return true // inside a [ ... ] label
  const lastLink = before.lastIndexOf('](')
  return lastLink !== -1 && !before.slice(lastLink + 2).includes(')') // inside ( url )
}

const looseTitlePattern = (title: string): string => {
  // Trim leading/trailing punctuation so the pattern starts and ends on a real
  // character (a trailing separator class would greedily swallow following
  // punctuation, e.g. "(Week 26))." ).
  const ascii = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '')
  let pat = ''
  for (const ch of ascii) {
    const lc = ch.toLowerCase()
    if (/[a-z0-9]/.test(lc)) {
      pat += ACCENT_CLASS[lc] ? `[${ACCENT_CLASS[lc]}]` : ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    } else {
      pat += '[^a-zA-Z0-9]*' // flexible separators: any run of non-alphanumerics (:, ;, —, ?, ", …)
    }
  }
  return pat
}

// Minimal, safe markdown → HTML for the assistant's output. We escape first,
// then re-introduce only: [label](http…) links (thebigwhale.io or https only),
// **bold**, and paragraph/line breaks. No raw HTML from the model is honoured.
const renderMarkdown = (raw: string): string => {
  let html = escapeHtml(raw)
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label: string, url: string) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  )
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  return html
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

export class AiChatComponent extends HTMLElement {
  private root: ShadowRoot
  private service = new AiChatService()
  private history: ChatTurn[] = []
  private busy = false
  private tracked = false
  private collapsed = false
  private abort: AbortController | null = null

  // NB: not named `lang` — that clashes with HTMLElement.lang (a string prop).
  private get currentLang(): Lang {
    const attr = this.getAttribute('lang')
    if (attr === 'fr' || attr === 'en') return attr
    return i18n.getLanguage() === 'fr' ? 'fr' : 'en'
  }
  private get t() { return COPY[this.currentLang] }

  // `layout="page"` = full-height variant for the dedicated /ai-assistant page:
  // the log grows to fill the viewport and the collapse control is dropped (a
  // whole page can't sensibly collapse itself).
  private get isPage(): boolean { return this.getAttribute('layout') === 'page' }

  constructor() {
    super()
    this.root = this.attachShadow({ mode: 'open' })
  }

  connectedCallback() {
    // The dedicated page is never collapsible, so don't inherit the home panel's
    // persisted collapsed state there.
    if (!this.isPage) {
      try { this.collapsed = localStorage.getItem(COLLAPSE_KEY) === '1' } catch { /* ignore */ }
    }
    this.render()
  }

  disconnectedCallback() {
    this.abort?.abort()
  }

  private render() {
    const t = this.t
    this.root.innerHTML = `
      <style>${STYLES}</style>
      <section class="panel${this.isPage ? ' panel--page' : ''}" part="panel">
        <header class="head">
          <div class="head__main">
            <div class="head__title">${t.title}</div>
            <div class="head__sub">${t.subtitle}</div>
          </div>
          <div class="head__actions">
            <button class="head__btn head__reset" type="button"
                    title="${escapeHtml(t.reset)}" aria-label="${escapeHtml(t.reset)}">↺</button>
            <button class="head__btn head__toggle" type="button"
                    title="${escapeHtml(t.hide)}" aria-label="${escapeHtml(t.hide)}">▾</button>
          </div>
        </header>
        <div class="log" role="log" aria-live="polite"></div>
        <div class="chips">
          ${['research', 'mica', 'stablecoins', 'events']
            .map((k) => `<button class="chip" data-q="${escapeHtml(t['q_' + k])}">${t['chip_' + k]}</button>`)
            .join('')}
        </div>
        <form class="composer">
          <input class="composer__input" type="text" autocomplete="off"
                 placeholder="${escapeHtml(t.placeholder)}" aria-label="${escapeHtml(t.title)}" />
          <button class="composer__send" type="submit">${t.send}</button>
        </form>
        <div class="disclaimer">${t.disclaimer}</div>
      </section>
    `

    const form = this.root.querySelector<HTMLFormElement>('.composer')!
    const input = this.root.querySelector<HTMLInputElement>('.composer__input')!
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const text = input.value.trim()
      if (!text) return
      input.value = ''
      void this.submit(text)
    })
    this.root.querySelectorAll<HTMLButtonElement>('.chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        if (this.busy) return
        void this.submit(chip.dataset.q ?? chip.textContent ?? '')
      })
    })

    // stopPropagation so these button clicks don't bubble to the header-level
    // expand handler below (which would re-open a panel the toggle just closed).
    this.root.querySelector<HTMLButtonElement>('.head__reset')!
      .addEventListener('click', (e) => { e.stopPropagation(); this.resetConversation() })
    this.root.querySelector<HTMLButtonElement>('.head__toggle')!
      .addEventListener('click', (e) => { e.stopPropagation(); this.setCollapsed(!this.collapsed) })
    // The whole header bar toggles the panel (same as the ▾ button), not just
    // the button — expand when collapsed, collapse when expanded. Disabled on
    // the dedicated /ai-assistant page (which never collapses).
    this.root.querySelector<HTMLElement>('.head')!
      .addEventListener('click', () => { if (!this.isPage) this.setCollapsed(!this.collapsed) })
    this.applyCollapsed()
  }

  private applyCollapsed() {
    if (this.isPage) return // dedicated page never collapses
    const panel = this.root.querySelector<HTMLElement>('.panel')
    if (panel) panel.classList.toggle('panel--collapsed', this.collapsed)
    const toggle = this.root.querySelector<HTMLButtonElement>('.head__toggle')
    if (toggle) {
      toggle.textContent = this.collapsed ? '▸' : '▾'
      const label = this.collapsed ? this.t.show : this.t.hide
      toggle.title = label
      toggle.setAttribute('aria-label', label)
    }
  }

  private setCollapsed(v: boolean) {
    this.collapsed = v
    try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0') } catch { /* ignore */ }
    this.applyCollapsed()
  }

  // Clear the transcript and start fresh. A new conversation should count again
  // in the usage beacon, so reset `tracked` too.
  private resetConversation() {
    this.abort?.abort()
    this.busy = false
    this.tracked = false
    this.history = []
    this.log.innerHTML = ''
    this.toggleChips(true)
    const input = this.root.querySelector<HTMLInputElement>('.composer__input')
    if (input) input.value = ''
  }

  private get log() { return this.root.querySelector<HTMLDivElement>('.log')! }

  private addBubble(role: 'user' | 'assistant', html: string): HTMLDivElement {
    const el = document.createElement('div')
    el.className = `bubble bubble--${role}`
    el.innerHTML = html
    this.log.appendChild(el)
    this.log.scrollTop = this.log.scrollHeight
    return el
  }

  // Turn each source title the model named in its answer into an inline link,
  // using the authoritative app URL from the `sources` event. The model never
  // writes URLs itself (so long slugs can't be truncated) — we splice them in.
  // Matching is case-, accent- and punctuation-insensitive so the tracker's
  // canonical name ("SOCIETE GENERALE - FORGE") links the model's rendering
  // ("Société Générale (Forge)").
  private linkifyTitles(text: string, sources: ChatSource[]): string {
    let out = text
    // Longest titles first so "Société Générale Forge" wins over "Société Générale".
    const sorted = [...sources].filter((s) => s.title && s.url).sort((a, b) => b.title.length - a.title.length)
    for (const s of sorted) {
      const re = new RegExp(`(\\*\\*)?(${looseTitlePattern(s.title)})(\\*\\*)?`, 'i')
      out = out.replace(re, (match: string, ...rest: unknown[]) => {
        const offset = rest[rest.length - 2] as number
        const full = rest[rest.length - 1] as string
        if (insideLink(full, offset)) return match
        return `[${match}](${s.url})`
      })
    }
    // Then link platform section names (first occurrence each), skipping any that
    // already sit inside a link produced above.
    for (const [re, url] of SECTION_LINKS) {
      out = out.replace(re, (match: string, offset: number, full: string) =>
        insideLink(full, offset) ? match : `[${match}](${url})`,
      )
    }
    return out
  }

  // Fire-and-forget product-usage beacon → cockpit "Visits by type" (type
  // chat_ai). Once per conversation. The chat is served by a separate backend,
  // so it must be reported explicitly to tbw-backend (attributed via the JWT).
  private trackChatVisit() {
    if (this.tracked) return
    this.tracked = true
    const token = localStorage.getItem('tbw-auth-infos')
    const api = import.meta.env.VITE_API_ENDPOINT
    if (!token || !api) return
    void fetch(`${api}usage/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ type: 'chat_ai' }),
      keepalive: true,
    }).catch(() => {})
  }

  private async submit(text: string) {
    if (this.busy) return
    this.busy = true
    this.trackChatVisit()
    this.toggleChips(false)

    this.addBubble('user', escapeHtml(text))
    const priorHistory = [...this.history]
    this.history.push({ role: 'user', content: text })

    const answer = this.addBubble(
      'assistant',
      `<span class="typing" role="status" aria-label="${escapeHtml(this.t.thinking)}"><i></i><i></i><i></i></span>`,
    )
    let acc = ''
    let sources: ChatSource[] = []
    this.abort = new AbortController()

    await this.service.ask(text, priorHistory, {
      signal: this.abort.signal,
      onChunk: (chunk) => {
        acc += chunk
        answer.innerHTML = renderMarkdown(acc)
        this.log.scrollTop = this.log.scrollHeight
      },
      // Authoritative app URLs — kept until the answer is complete, then spliced
      // onto the titles the model named, inline in the text (no separate section).
      onSources: (s) => { sources = s },
      onDone: () => {
        if (!acc) {
          answer.remove()
        } else {
          answer.innerHTML = renderMarkdown(this.linkifyTitles(acc, sources))
          this.log.scrollTop = this.log.scrollHeight
          this.history.push({ role: 'assistant', content: acc })
        }
        this.busy = false
        this.toggleChips(true)
      },
      onError: () => {
        answer.classList.add('bubble--error')
        answer.textContent = this.t.error
        // Roll back the unanswered user turn so history stays consistent.
        this.history.pop()
        this.busy = false
        this.toggleChips(true)
      },
    })
  }

  private toggleChips(enabled: boolean) {
    this.root.querySelectorAll<HTMLButtonElement>('.chip').forEach((c) => (c.disabled = !enabled))
    const send = this.root.querySelector<HTMLButtonElement>('.composer__send')
    if (send) send.disabled = !enabled
  }
}

const STYLES = /* css */ `
  :host {
    --tbw-accent: var(--text-color--text-accent, #3442eb);
    --tbw-navy: #161032;
    --tbw-ice: var(--background-color--background-tertiary, #f7f7fa);
    --tbw-border: var(--border-secondary, #e3e2ee);
    --tbw-text: var(--text-color--text-primary, #161032);
    --tbw-muted: var(--text-color--text-secondary, #6b6980);
    display: block;
    font-family: Inter, system-ui, -apple-system, sans-serif;
  }
  .panel {
    border: 1px solid var(--tbw-border);
    border-radius: 16px;
    background: #fff;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .head__title { font-size: 1.05rem; font-weight: 700; color: var(--tbw-navy); }
  .head__sub { font-size: 0.85rem; color: var(--tbw-muted); margin-top: 2px; }
  .head__actions { display: flex; gap: 4px; flex-shrink: 0; }
  .head__btn {
    border: 1px solid var(--tbw-border);
    background: #fff;
    color: var(--tbw-muted);
    width: 28px;
    height: 28px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .head__btn:hover { background: var(--tbw-ice); border-color: var(--tbw-accent); color: var(--tbw-accent); }
  .panel--collapsed { gap: 0; }
  .panel:not(.panel--page) .head { cursor: pointer; }
  .panel--collapsed .head__sub,
  .panel--collapsed .head__reset,
  .panel--collapsed .log,
  .panel--collapsed .chips,
  .panel--collapsed .composer,
  .panel--collapsed .disclaimer { display: none; }
  /* Full-height variant for the dedicated /ai-assistant page: the log fills the
     available space and the composer sits at the bottom. */
  .panel--page { height: 100%; min-height: 70vh; }
  .panel--page .head__toggle { display: none; }
  .panel--page .log { flex: 1 1 auto; max-height: none; }
  .panel--page .log:empty { display: flex; } /* keep the empty area open so the composer stays at the bottom */
  .log {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 340px;
    overflow-y: auto;
    scroll-behavior: smooth;
  }
  .log:empty { display: none; }
  .bubble {
    font-size: 0.9rem;
    line-height: 1.5;
    padding: 10px 14px;
    border-radius: 12px;
    max-width: 90%;
    word-wrap: break-word;
  }
  .bubble p { margin: 0 0 0.5em; }
  .bubble p:last-child { margin-bottom: 0; }
  .bubble a { color: var(--tbw-accent); text-decoration: underline; }
  .bubble--user {
    align-self: flex-end;
    background: var(--tbw-accent);
    color: #fff;
  }
  .bubble--user a { color: #fff; }
  .bubble--assistant {
    align-self: flex-start;
    background: var(--tbw-ice);
    color: var(--tbw-text);
  }
  .bubble--error { background: #fff0f0; color: #b42318; }
  .typing { display: inline-flex; gap: 5px; align-items: center; height: 1.2em; }
  .typing i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--tbw-accent);
    opacity: 0.35;
    animation: tbw-typing 1.3s infinite ease-in-out both;
  }
  .typing i:nth-child(2) { animation-delay: 0.18s; }
  .typing i:nth-child(3) { animation-delay: 0.36s; }
  @keyframes tbw-typing {
    0%, 70%, 100% { opacity: 0.3; transform: translateY(0); }
    35% { opacity: 1; transform: translateY(-4px); }
  }
  @media (prefers-reduced-motion: reduce) {
    .typing i { animation-duration: 0.01ms; animation-iteration-count: 1; opacity: 0.7; }
  }
  .chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .chip {
    border: 1px solid var(--tbw-border);
    background: #fff;
    color: var(--tbw-navy);
    font-size: 0.8rem;
    padding: 6px 12px;
    border-radius: 999px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .chip:hover:not(:disabled) { background: var(--tbw-ice); border-color: var(--tbw-accent); }
  .chip:disabled { opacity: 0.5; cursor: default; }
  .composer { display: flex; gap: 8px; }
  .composer__input {
    flex: 1;
    border: 1px solid var(--tbw-border);
    border-radius: 10px;
    padding: 11px 14px;
    font-size: 0.9rem;
    font-family: inherit;
    color: var(--tbw-text);
    outline: none;
  }
  .composer__input:focus { border-color: var(--tbw-accent); }
  .composer__send {
    background: var(--tbw-accent);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0 20px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
  }
  .composer__send:disabled { opacity: 0.5; cursor: default; }
  .disclaimer { font-size: 0.72rem; color: var(--tbw-muted); }
`
